import { useEffect, useState } from "react";
import {
  Box,
  LinearProgress,
  Typography,
  CircularProgress,
  Alert,
  Skeleton,
} from "@mui/material";
import { LightTooltip } from "../LightTooltip";
import {
  fetchCreditBalance,
  CreditBalanceResponse,
} from "../../apis/exportTrackingApi";
import { isReadOnlyUser } from "../../utils/auth";

interface CreditProgressBarProps {
  tooltipMessage?: string;
  usedCredits?: number;
  totalCredits?: number;
  variant?: "opportunityGenerator" | "company";
  loadingVariant?: "spinner" | "skeleton";
  compact?: boolean;
}

/**
 * CreditProgressBar component displays a progress bar showing credit usage
 * It fetches credit information from the API when no props are provided
 *
 * @param loadingVariant - Controls the loading state appearance:
 *   - "spinner": Shows a circular progress spinner (default)
 *   - "skeleton": Shows skeleton placeholders matching the final layout
 */
export default function CreditProgressBar(props: CreditProgressBarProps) {
  const {
    tooltipMessage = "Credits are shared between all users and delivery channels activated by your organization",
    usedCredits: propUsedCredits,
    totalCredits: propTotalCredits,
    variant = "opportunityGenerator",
    loadingVariant = "spinner",
    compact = false,
  } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [hidden, setHidden] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [creditData, setCreditData] = useState<CreditBalanceResponse | null>(
    typeof props.totalCredits === "number" ||
      typeof props.usedCredits === "number"
      ? {
          credits: props.totalCredits || 0,
          balance: (props.totalCredits || 0) - (props.usedCredits || 0),
        }
      : null
  );

  useEffect(() => {
    // Read-only users don't consume credits, so the balance is meaningless to
    // them — skip the fetch and render nothing (see early return below).
    if (isReadOnlyUser()) {
      setLoading(false);
      return;
    }

    if (propUsedCredits !== undefined && propTotalCredits !== undefined) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchCreditBalance()
      .then((data) => {
        setCreditData(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setLoading(false);
          setHidden(true);
          return;
        }

        setError("Failed to load credit information");
        setLoading(false);
      });
  }, [propUsedCredits, propTotalCredits]);

  const totalCredits =
    propTotalCredits !== undefined
      ? propTotalCredits
      : creditData?.credits || 0;

  const creditBalance =
    creditData?.balance !== undefined ? creditData.balance : 0;

  const usedCredits =
    propUsedCredits !== undefined
      ? propUsedCredits
      : totalCredits - creditBalance;

  const percentage =
    usedCredits >= totalCredits
      ? 100
      : Math.min(Math.round((usedCredits / totalCredits) * 100), 100) || 0;

  const creditsText = `${usedCredits.toLocaleString()} of ${totalCredits.toLocaleString()} credits`;

  if (hidden || isReadOnlyUser()) {
    return null;
  }

  const containerSx = {
    display: "flex",
    alignItems: "center",
    padding: 0,
    gap: 1.5,
    height: "100%",
    ...(compact
      ? {}
      : {
          marginBottom: "10px",
          marginRight: variant === "opportunityGenerator" ? "15px" : 0,
        }),
  };

  if (loading) {
    return (
      <Box sx={containerSx}>
        {loadingVariant === "skeleton" ? (
          <>
            <Skeleton
              variant="rectangular"
              width={100}
              height={8}
              sx={{ borderRadius: 1 }}
            />
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={containerSx}>
        <Alert
          severity="error"
          sx={{
            fontSize: "0.75rem",
            padding: "4px 8px",
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <LightTooltip
      title={
        <Box sx={{ padding: 1, maxWidth: 200 }}>
          <Typography variant="body2" sx={{ color: "black" }}>
            {tooltipMessage}
          </Typography>
        </Box>
      }
    >
      <Box sx={containerSx}>
        <Box
          sx={{
            position: "relative",
            width: 100,
            display: "flex",
            alignItems: "center",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 8,
              borderRadius: 1,
              backgroundColor: "#DFF3FF",
              width: "100%",
              "& .MuiLinearProgress-bar": {
                backgroundColor: creditBalance <= 0 ? "#E25C83" : "#1DA5C3",
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#5F6A73",
            whiteSpace: "nowrap",
            fontWeight: 400,
            lineHeight: 1,
          }}
        >
          {creditsText}
        </Typography>
      </Box>
    </LightTooltip>
  );
}
